(() => {
  if (customElements.get('email-popups')) return;
  const prefix = 'histrips:email-popups:';
  const read = (key) => { try { return localStorage.getItem(prefix + key); } catch { return null; } };
  const write = (key, value) => { try { localStorage.setItem(prefix + key, value); return true; } catch { return false; } };
  const lines = (value) => String(value || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  class EmailPopups extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.popups = [...this.querySelectorAll('dialog')];
      this.messages = JSON.parse(this.querySelector('[data-messages]')?.textContent || '{}');
      this.editor = Boolean(window.Shopify?.designMode);
      this.preview = window.Shopify?.theme?.role === 'unpublished' && new URLSearchParams(location.search).get('email_popup_preview') === '1';
      this.previewStep = this.preview && ['reward', 'sms', 'sms-success'].includes(new URLSearchParams(location.search).get('email_popup_step')) ? new URLSearchParams(location.search).get('email_popup_step') : null;
      if (!this.editor && !this.preview) this.recordVisit();
      this.popups.forEach(popup => {
        popup.rules = JSON.parse(popup.querySelector('[data-rules]').textContent);
        popup.querySelector('[data-close]').addEventListener('click', () => popup.close(), { signal });
        popup.addEventListener('click', event => {
          if (event.target !== popup) return;
          const r = popup.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) popup.close();
        }, { signal });
        // close events are queued; a verification challenge may reopen the same
        // dialog without ending the signup session.
        popup.addEventListener('close', () => { if (!popup.open) this.onClose(popup); }, { signal });
        popup.addEventListener('keydown', event => {
          if (event.key !== 'Tab') return;
          const stops = [...popup.querySelectorAll('button, a[href], input, select, textarea, iframe, [tabindex]')]
            .filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
          const first = stops[0], last = stops.at(-1);
          if (!first) { event.preventDefault(); popup.focus(); return; }
          if (event.shiftKey && (document.activeElement === first || document.activeElement === popup)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first.focus();
          }
        }, { signal });
        popup.querySelector('[data-reveal]').addEventListener('click', () => this.reveal(popup), { signal });
        popup.querySelector('[data-claim]').addEventListener('click', () => this.showForm(popup), { signal });
        popup.querySelector('[data-retry]').addEventListener('click', () => this.watchForm(popup), { signal });
        popup.querySelector('[data-copy]').addEventListener('click', () => this.copyCode(popup), { signal });
        popup.querySelector('[data-save-more]').addEventListener('click', () => this.showStep(popup, 'sms'), { signal });
        popup.querySelectorAll('[data-back]').forEach(button => button.addEventListener('click', () => this.showStep(popup, 'reward'), { signal }));
        popup.querySelector('[data-finish]').addEventListener('click', () => popup.close(), { signal });
        popup.querySelector('[data-sms-form]').addEventListener('submit', event => this.subscribeSms(event, popup), { signal });
        popup.querySelector('[name="phone"]').addEventListener('input', event => {
          event.target.setCustomValidity('');
          popup.querySelector('[data-sms-error]').hidden = true;
        }, { signal });
      });
      window.addEventListener('klaviyoForms', event => this.onKlaviyo(event), { signal });
      window.addEventListener('storage', () => {
        if (this.active && !this.editor && !this.preview && read('subscribed') && this.active.rules.hideSubscribers) this.active.close();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.ready) this.scheduleOpen();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (!this.editor) return;
        const popup = this.popups.find(p => p.dataset.blockId === event.detail.blockId);
        if (popup) this.open(popup);
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (this.editor && event.target.contains(this) && this.popups[0]) this.open(this.popups[0]);
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (this.editor && event.target.contains(this)) this.active?.close();
      }, { signal });
      if (!this.editor && this.dataset.enabled === 'true') {
        this.timer = setTimeout(() => { this.ready = true; this.scheduleOpen(); }, Number(this.dataset.delay) * 1000);
      }
    }

    disconnectedCallback() {
      this.active?.close();
      this.restoreScroll();
      this.abort?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.formTimer);
      clearTimeout(this.retryTimer);
      this.formObserver?.disconnect();
      this.scratchObserver?.disconnect();
      this.challengeObserver?.disconnect();
    }

    recordVisit() {
      let visit;
      try { visit = JSON.parse(read('visit')); } catch { /* Ignore invalid storage. */ }
      const now = Date.now();
      this.newVisit = !visit || (visit.first === true && now - visit.last < 30 * 60 * 1000);
      const source = new URLSearchParams(location.search);
      const campaign = ['utm_source', 'utm_medium'].some(key => /^(klaviyo|email|sms|text)$/i.test(source.get(key) || ''));
      if (campaign || /\/(account|thank_you)(\/|$)/.test(location.pathname)) write('known', 'true');
      // Store on all pages, including those that do not qualify for a popup.
      if (!write('visit', JSON.stringify({ first: this.newVisit, last: now }))) this.newVisit = false;
    }

    matches(popup) {
      const r = popup.rules;
      const path = location.pathname; // Query strings must never satisfy path conditions.
      if (!r.enabled || !/^[a-zA-Z0-9]+$/.test(popup.dataset.formId)) return false;
      if (!this.preview && r.newVisitors && (!this.newVisit || this.dataset.customer === 'true' || read('known'))) return false;
      if (!this.preview && r.hideSubscribers && (this.dataset.subscribed === 'true' || read('subscribed') || localStorageSafe('theme:popup-filled') === 'true')) return false;
      if (!this.preview && read('shown')) return false;
      if (r.products?.length && !r.products.includes(this.dataset.product)) return false;
      const included = lines(r.includePaths);
      if (included.length && !included.some(fragment => path.includes(fragment))) return false;
      if (lines(r.excludePaths).some(fragment => path.includes(fragment))) return false;
      const countries = String(r.countries || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
      if (countries.length) {
        if (!this.dataset.country) return false;
        const inCountry = countries.includes(this.dataset.country.toUpperCase());
        if (r.countryMode === 'exclude' ? inCountry : !inCountry) return false;
      }
      const mobile = matchMedia('(max-width: 749px)').matches;
      return !(r.devices === 'mobile' && !mobile || r.devices === 'desktop' && mobile);
    }

    hasBlockingOverlay() {
      return [...document.querySelectorAll('dialog[open], .drawer[open], [aria-modal="true"], .mh1--mega-open, .mh1--mobile-mega-open')].some(element => {
        const style = getComputedStyle(element);
        return element.getClientRects().length && style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
      });
    }

    async scheduleOpen() {
      if (this.checking || this.active || !this.isConnected || document.hidden || this.editor) return;
      const popup = this.popups.find(p => this.matches(p));
      if (!popup) return;
      // Do not interrupt the cart, search, or another modal.
      if (this.hasBlockingOverlay()) {
        clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => this.scheduleOpen(), 1000);
        return;
      }
      this.checking = true;
      let known = false;
      if (!this.preview && (popup.rules.newVisitors || popup.rules.hideSubscribers) && typeof window.klaviyo?.isIdentified === 'function') {
        // Klaviyo exposes identity, not a public subscription lookup. Suppress all
        // identified profiles conservatively; never infer consent from a cookie.
        let identityTimer;
        try {
          known = await Promise.race([
            window.klaviyo.isIdentified(),
            new Promise(resolve => { identityTimer = setTimeout(() => resolve(null), 1500); })
          ]);
        } catch { known = null; }
        clearTimeout(identityTimer);
      }
      this.checking = false;
      if (known === true) { write('known', 'true'); return; }
      if (known === null) {
        // An unavailable identity service is not evidence of a known visitor.
        // Retry briefly without poisoning eligibility on future page loads.
        this.identityRetries = (this.identityRetries || 0) + 1;
        if (this.identityRetries <= 3 && this.isConnected) {
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => this.scheduleOpen(), 2000);
        }
        return;
      }
      if (this.isConnected && !document.hidden && this.matches(popup)) {
        if (this.hasBlockingOverlay()) {
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => this.scheduleOpen(), 1000);
        } else this.open(popup);
      }
    }

    open(popup) {
      if (this.active === popup && popup.open) return;
      if (this.active) { const previous = this.active; previous.close(); this.onClose(previous); }
      this.active = popup;
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      popup.showModal();
      popup.focus({ preventScroll: true });
      this.watchChallenge(popup);
      if (!this.editor && !this.preview) write('shown', 'true');
      if (this.previewStep) {
        popup.querySelector('[data-preview-note]').hidden = false;
        this.showStep(popup, this.previewStep);
      } else if (!popup.scratchReady) this.setupScratch(popup);
    }

    restoreScroll() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }

    watchChallenge(popup) {
      this.challengeObserver?.disconnect();
      const update = () => {
        if (this.active !== popup || !popup.open) return;
        const challenge = document.querySelector('iframe[title="Verification system"][src^="https://geo.captcha-delivery.com/"]');
        if (challenge && !popup.challengePaused) {
          // Klaviyo renders its verification outside this native modal. Release
          // modality so the real challenge remains visible and interactive; do
          // not move, reload, solve or dismiss the provider's iframe.
          popup.challengePaused = true;
          popup.close();
          popup.show();
          popup.inert = true;
          popup.style.visibility = 'hidden';
          challenge.focus();
        } else if (!challenge && popup.challengePaused) {
          popup.challengePaused = false;
          popup.inert = false;
          popup.style.visibility = '';
          popup.close();
          popup.showModal();
          popup.focus({ preventScroll: true });
        }
      };
      this.challengeObserver = new MutationObserver(update);
      this.challengeObserver.observe(document.body, { childList: true, subtree: true });
      update();
    }

    onClose(popup) {
      if (this.active !== popup) return;
      this.challengeObserver?.disconnect();
      popup.challengePaused = false;
      popup.inert = false;
      popup.style.visibility = '';
      this.restoreScroll();
      this.active = null;
      popup.email = null;
      popup.smsRequest?.abort();
      popup.querySelector('[data-sms-form]').reset();
      clearTimeout(this.formTimer);
      this.formObserver?.disconnect();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }

    setupScratch(popup) {
      popup.scratchReady = true;
      const canvas = popup.querySelector('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) { this.reveal(popup); return; }
      const cover = new Image();
      cover.crossOrigin = 'anonymous';
      const paint = () => {
        if (popup.revealed) return;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width) return;
        // Fixed working resolution makes threshold sampling cheap and independent
        // of retina screens. Browser scales pointer coordinates into this space.
        canvas.width = 600;
        canvas.height = 440;
        context.globalCompositeOperation = 'source-over';
        context.fillStyle = '#d3af4f';
        context.fillRect(0, 0, 600, 440);
        if (cover.complete && cover.naturalWidth) context.drawImage(cover, 0, 0, 600, 440);
        else {
          context.fillStyle = '#fff3ba';
          context.font = 'bold 46px Arial';
          context.textAlign = 'center';
          context.fillText('SCRATCH HERE', 300, 240);
        }
      };
      cover.onload = () => { if (!popup.scratchStarted) paint(); };
      cover.src = canvas.dataset.cover;
      paint();
      let previous = null;
      let distance = 0;
      const point = event => {
        const rect = canvas.getBoundingClientRect();
        return { x: (event.clientX - rect.left) * 600 / rect.width, y: (event.clientY - rect.top) * 440 / rect.height };
      };
      const scratch = event => {
        if (!previous || popup.revealed) return;
        const next = point(event);
        popup.scratchStarted = true;
        context.globalCompositeOperation = 'destination-out';
        context.lineWidth = 65;
        context.lineCap = 'round';
        context.beginPath();
        context.moveTo(previous.x, previous.y);
        context.lineTo(next.x, next.y);
        context.stroke();
        distance += Math.hypot(next.x - previous.x, next.y - previous.y);
        previous = next;
      };
      const finish = () => {
        if (!previous || popup.revealed) return;
        previous = null;
        const threshold = Number(popup.querySelector('[data-scratch]').dataset.threshold) / 100;
        try {
          const pixels = context.getImageData(0, 0, 600, 440).data;
          let clear = 0, sampled = 0;
          for (let i = 3; i < pixels.length; i += 64) { sampled++; if (pixels[i] < 128) clear++; }
          if (clear / sampled >= threshold) this.reveal(popup);
        } catch {
          // A merchant image may not permit pixel reads. Keep reveal usable.
          if (distance > 600 * 440 * threshold / 45) this.reveal(popup);
        }
      };
      const signal = this.abort.signal;
      canvas.addEventListener('pointerdown', event => {
        if (event.button !== 0 || popup.revealed) return;
        previous = point(event);
        canvas.setPointerCapture(event.pointerId);
        event.preventDefault();
      }, { signal });
      canvas.addEventListener('pointermove', scratch, { signal });
      canvas.addEventListener('pointerup', finish, { signal });
      canvas.addEventListener('pointercancel', finish, { signal });
      canvas.addEventListener('lostpointercapture', finish, { signal });
    }

    reveal(popup) {
      if (popup.revealed) return;
      popup.revealed = true;
      popup.querySelector('[data-scratch]').classList.add('is-revealed');
      popup.querySelector('[data-reveal]').hidden = true;
      popup.querySelector('[data-claim]').hidden = false;
      popup.querySelector('.ep__prize').removeAttribute('aria-hidden');
      popup.querySelector('[data-announcement]').textContent = popup.querySelector('.ep__prize').textContent.trim();
      popup.querySelector('[data-claim]').focus({ preventScroll: true });
    }

    showForm(popup) {
      popup.formShown = true;
      popup.querySelector('[data-game]').hidden = true;
      const form = popup.querySelector('[data-form]');
      form.removeAttribute('data-staged');
      form.removeAttribute('inert');
      form.removeAttribute('aria-hidden');
      const heading = popup.querySelector('[data-form-heading]');
      heading.id = popup.id + '-form-title';
      popup.setAttribute('aria-labelledby', heading.id);
      heading.focus({ preventScroll: true });
      this.watchForm(popup);
    }

    watchForm(popup) {
      this.formObserver?.disconnect();
      clearTimeout(this.formTimer);
      const embed = popup.querySelector('[data-embed]');
      const status = popup.querySelector('[data-form-status]');
      popup.loadingLabel ||= status.textContent;
      status.hidden = false;
      status.textContent = popup.loadingLabel;
      popup.querySelector('[data-retry]').hidden = true;
      const ready = () => {
        if (!embed?.querySelector('input, button, form, iframe')) return false;
        status.hidden = true;
        popup.querySelector('[data-retry]').hidden = true;
        clearTimeout(this.formTimer);
        this.formObserver?.disconnect();
        return true;
      };
      if (ready()) return;
      if (embed) {
        this.formObserver = new MutationObserver(ready);
        this.formObserver.observe(embed, { childList: true, subtree: true });
      }
      this.formTimer = setTimeout(() => {
        if (ready()) return;
        status.textContent = this.querySelector('[data-form-error]').textContent;
        popup.querySelector('[data-retry]').hidden = false;
      }, 10000);
    }

    showStep(popup, step) {
      // Keep Klaviyo mounted, but hide its old success/coupon screen completely.
      // Only an actual email conversion can enter this reward flow.
      const form = popup.querySelector('[data-form]');
      form.removeAttribute('data-staged');
      form.hidden = true;
      form.inert = true;
      form.setAttribute('aria-hidden', 'true');
      popup.querySelector('[data-game]').hidden = true;
      for (const name of ['reward', 'sms', 'sms-success']) {
        popup.querySelector(`[data-${name}]`).hidden = name !== step;
      }
      popup.step = step;
      const heading = popup.querySelector(`[data-${step === 'sms-success' ? 'success' : step}-heading]`);
      heading.id = popup.id + '-' + step + '-title';
      popup.removeAttribute('aria-label');
      popup.setAttribute('aria-labelledby', heading.id);
      heading.focus({ preventScroll: true });
      popup.querySelector('[data-more]').hidden = popup.smsSubmitted || (!popup.email && !this.previewStep) ||
        !/^[a-zA-Z0-9]{6}$/.test(this.dataset.companyId || '') || !/^[a-zA-Z0-9]+$/.test(popup.dataset.listId || '');
      clearTimeout(this.formTimer);
      this.formObserver?.disconnect();
    }

    async copyCode(popup) {
      const status = popup.querySelector('[data-copy-status]');
      try {
        await navigator.clipboard.writeText(popup.dataset.discountCode);
        status.textContent = this.messages.copied;
      } catch {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(popup.querySelector('[data-code]'));
        selection?.removeAllRanges();
        selection?.addRange(range);
        status.textContent = this.messages.copyError;
      }
    }

    async subscribeSms(event, popup) {
      event.preventDefault();
      const form = event.currentTarget;
      if (this.previewStep) return;
      if (popup.smsRequest || popup.smsSubmitted || this.editor || popup.step !== 'sms') return;
      const phone = form.elements.phone;
      const number = phone.value.trim().replace(/[\s().-]/g, '');
      phone.setCustomValidity(/^\+[1-9]\d{7,14}$/.test(number) ? '' : this.messages.phoneError);
      if (!form.reportValidity()) return;
      const error = popup.querySelector('[data-sms-error]');
      const button = popup.querySelector('[data-sms-submit]');
      error.hidden = true;
      if (!popup.email || !/^[a-zA-Z0-9]{6}$/.test(this.dataset.companyId || '') || !/^[a-zA-Z0-9]+$/.test(popup.dataset.listId || '')) {
        error.textContent = this.messages.smsError;
        error.hidden = false;
        return;
      }
      const label = button.textContent;
      button.disabled = true;
      button.textContent = this.messages.smsLoading;
      form.setAttribute('aria-busy', 'true');
      const controller = new AbortController();
      popup.smsRequest = controller;
      // Give a shopper time to complete Klaviyo's browser verification.
      let timeout;
      const checkTimeout = () => {
        if (popup.challengePaused) timeout = setTimeout(checkTimeout, 15000);
        else controller.abort();
      };
      timeout = setTimeout(checkTimeout, 15000);
      try {
        // The public client endpoint records SMS marketing consent. Including the
        // email links both channels; it does NOT opt the email in again. No PII is
        // persisted in browser storage, URLs, logs or analytics.
        const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.dataset.companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          signal: controller.signal,
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: {
              custom_source: 'HiStrips popup SMS — ' + popup.dataset.blockId,
              profile: { data: { type: 'profile', attributes: {
                email: popup.email,
                phone_number: number,
                subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
              } } }
            },
            relationships: { list: { data: { type: 'list', id: popup.dataset.listId } } }
          } })
        });
        if (response.status !== 202) throw new Error('Subscription not accepted');
        popup.smsSubmitted = true;
        form.reset();
        popup.email = null;
        // Accepted is not proof of completed double opt-in. The success message
        // asks the shopper to confirm any text they receive.
        if (this.active === popup && popup.open) {
          if (popup.step === 'sms') this.showStep(popup, 'sms-success');
          else popup.querySelector('[data-more]').hidden = true;
        }
      } catch {
        if (this.active === popup && popup.open) {
          error.textContent = this.messages.smsError;
          error.hidden = false;
        }
      } finally {
        clearTimeout(timeout);
        popup.smsRequest = null;
        button.disabled = false;
        button.textContent = label;
        form.removeAttribute('aria-busy');
      }
    }

    onKlaviyo(event) {
      if (this.editor) return;
      const { type, formId } = event.detail || {};
      if (type !== 'submit') return;
      // Listen to all Klaviyo signups to suppress acquisition popups later.
      if (!this.preview) write('subscribed', 'true');
      const popup = this.active;
      if (!popup) return;
      if (formId !== popup.dataset.formId) {
        if (popup.rules.hideSubscribers) popup.close();
        return;
      }
      if (popup.dataset.discountCode && popup.formShown) {
        // Support the documented email key and the $email field emitted by the
        // store's current published embeds.
        // Never infer a subscription from a button click or a stepSubmit event.
        const email = event.detail.metaData?.email || event.detail.metaData?.$email;
        popup.email = typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
        this.showStep(popup, 'reward');
        return;
      }
      // submit is the FIRST conversion, not the end of a multi-step form.
      // Leave Klaviyo mounted so SMS consent, Skip and success keep working.
      popup.querySelector('[data-form-heading]').hidden = true;
      popup.setAttribute('aria-label', 'Complete your signup');
      popup.removeAttribute('aria-labelledby');
    }
  }
  function localStorageSafe(key) { try { return localStorage.getItem(key); } catch { return null; } }
  customElements.define('email-popups', EmailPopups);
})();
